// ============================================================================
// @orgazone/tickets — ÉCHANTILLONS (portés de ticket-kit.jsx / ticket-gabarits.jsx).
// Placeholders de maquette ; en production émetteur/logiciel viennent du back-office.
// Argent en CENTIMES. Sert aux golden tests et à l'aperçu.
// ============================================================================
import type { TicketContext } from './builders';
import type {
  EmitterInfo, JustificatifPart, MiseEnAttenteInput, OuvertureInput, PrelevementInput,
  RetoucheInput, SaleTicketInput, SoftwareInfo, VoucherInput, ZInput,
} from './types';

const SAMPLE_EMITTER: EmitterInfo = {
  name: 'ORGAZONE',
  logo: null,
  address: ['12 rue de la Gare', '68000 COLMAR', 'FRANCE'],
  phone: 'Tél : 03 89 12 34 56',
  hours: 'Ouvert du mardi au samedi · 10h-19h',
  siret: 'SIRET 225 425 220 00018',
  naf: 'NAF 4771Z',
  tva: 'TVA FR33 535375',
  gln: 'GLN 3760000000017',
  footerReturn: "Échange ou avoir sous 30 jours sur présentation du ticket — articles non portés, non lavés, étiquettes d'origine.",
  footerThanks: 'Merci de votre visite et à bientôt !',
  validityAvoir: 'Avoir valable 1 an à compter de sa date d\'émission.',
  validityGift: 'Chèque cadeau valable 6 mois à compter de sa date d\'émission.',
};

// ⚠️ certNumber = PLACEHOLDER tant que la certification NF525 réelle n'est pas obtenue (pré-prod).
const SAMPLE_SOFT: SoftwareInfo = { name: 'Sliderz', version: '1.0.0', category: 'B', certNumber: 'LNE-0262' };

export const SAMPLE_CONTEXT: TicketContext = { emitter: SAMPLE_EMITTER, soft: SAMPLE_SOFT, paperWidthMm: 80 };

export const SAMPLE_SALE: SaleTicketInput = {
  num: 'T-S1R1-000147',
  shortNum: '147',
  register: 1,
  date: '30/06/2026',
  time: '10:45',
  seller: 'Camille',
  operator: 'Camille',
  client: { name: 'AATIFI Youssef', card: '6807997000' },
  lines: [
    { name: 'SAC JEANNE LULU', sub: 'CASTAGNETTE · TU CAMEL', code: '3760208543675', qty: 1, unitCents: 6999, vatRateBp: 2000 },
    { name: 'BONNET THERMONET', sub: 'BUFF · Taille U', code: '3760208501231', qty: 2, unitCents: 2250, vatRateBp: 2000, discountBp: 1000 },
    { name: 'GOURDE INOX 0,5L', sub: 'KLEAN · Acier', code: '3760208588881', qty: 1, unitCents: 2490, vatRateBp: 550 },
  ],
  globalDiscountBp: 0,
  payments: [{ method: 'Carte bancaire', amountCents: 13539 }],
  loyalty: { earnedCents: 185, balanceCents: 2686 },
  // Signature ECDSA P-256 réaliste (base64 ~88 car.) — démontre la restitution COMPACTE au ticket.
  signature: 'MEQCIH9k4Zt7f2aQ8bV1nR5wYpKoL9cJhGmN0sTu6vWx2yzAiA1BdEfGhIjKlMnOpQrStUvWxYz0123456789abcd==',
};

/** Vente d'un chèque cadeau : l'ENCAISSEMENT (titre en ligne TVA 0 %) — pièce DISTINCTE du titre. */
export const SAMPLE_GIFT_SALE: SaleTicketInput = {
  num: 'T-S1R1-000149',
  shortNum: '149',
  register: 1,
  date: '30/06/2026',
  time: '11:02',
  seller: 'Camille',
  operator: 'Camille',
  client: null,
  lines: [{ name: 'CHÈQUE CADEAU', sub: 'Titre n° CC-74220-1-000088', code: 'CC880142X', qty: 1, unitCents: 10000, vatRateBp: 0, title: true }],
  globalDiscountBp: 0,
  payments: [{ method: 'Carte bancaire', amountCents: 10000 }],
  loyalty: null,
  signature: 'S2V5-0149-7C1D-44E2',
};

export const SAMPLE_JUSTIF_PART: JustificatifPart = {
  client: 'DUPONT Marie', amountCents: 5000, method: 'Carte bancaire', num: 'JDP-74220-1-000061',
};

export const SAMPLE_TITRE_GIFT: VoucherInput = {
  bandLabel: 'CHÈQUE CADEAU', num: 'CC-74220-1-000088', code: 'CC880142X', date: '30/06/2026',
  amountCents: 10000, amountLabel: 'Valeur du chèque cadeau', originPiece: 'T-S1R1-000149',
  validity: SAMPLE_EMITTER.validityGift, expiry: '30/12/2026',
};

export const SAMPLE_TITRE_AVOIR: VoucherInput = {
  bandLabel: 'AVOIR', num: 'AV-74220-1-000204', code: 'AV204871X', date: '30/06/2026',
  amountCents: 4500, remainingCents: 4500, amountLabel: "Montant de l'avoir", source: 'Retour ticket T-…-000142',
  originPiece: 'T-74220-1-000142 (retour)', validity: SAMPLE_EMITTER.validityAvoir, expiry: '30/06/2027',
};

export const SAMPLE_MISE_EN_ATTENTE: MiseEnAttenteInput = {
  num: 'T-S1R1-000147', shortNum: '147', register: 1, date: '30/06/2026', time: '10:45', seller: 'Camille',
  client: { name: 'AATIFI Youssef' }, lines: SAMPLE_SALE.lines, globalDiscountBp: 0,
};

export const SAMPLE_Z: ZInput = {
  num: 'Z-74220-1-000031', date: '30/06/2026', period: 'Journée du 30/06/2026', register: 1, operator: 'Camille',
  open: '08:30', close: '19:45', caCents: 284760, nbTickets: 42, nbItems: 96,
  vat: [
    { rateBp: 2000, ttcCents: 258060, htCents: 215050, vatCents: 43010 },
    { rateBp: 550, ttcCents: 24708, htCents: 23420, vatCents: 1288 },
  ],
  pay: [
    { label: 'Espèces', amountCents: 51240 },
    { label: 'Carte bancaire', amountCents: 210520 },
    { label: 'Chèque', amountCents: 18000 },
    { label: 'Avoir', amountCents: 5000 },
  ],
  fondCents: 15000, cashSalesCents: 51240, prelevementsCents: 20000,
  theoCents: 46240, compteCents: 46000, varianceCents: -240,
  gtPerpetuelCents: 128453075,
  // Signature ECDSA réaliste (base64) — restitution COMPACTE au ticket Z (cf. SAMPLE_SALE).
  signature: 'MEUCIQC1z9BdEfGhIjKlMnOpQrStUvWxYz0123456789abcdKoL9cJhGmN0sTu6vWx2yzAh8k4Zt7f2aQ8bV1nR5w==',
};

export const SAMPLE_OUVERTURE: OuvertureInput = {
  num: 'OUV-74220-1-000031', date: '30/06/2026', time: '08:30', register: 1, operator: 'Camille',
  fondCents: 15000, coinsCents: 4200, notesCents: 10800, signature: 'S2V5-OUV0-0031-3D5E',
};

export const SAMPLE_PRELEVEMENT: PrelevementInput = {
  num: 'PRL-74220-1-000007', date: '30/06/2026', time: '15:12', register: 1, operator: 'Camille',
  type: 'Remise au coffre', amountCents: 20000, beforeCents: 66240, afterCents: 46240,
  motif: 'Sécurisation tiroir', signature: 'S2V5-PRL0-0007-6E2A',
};

export const SAMPLE_RETOUCHE: RetoucheInput = {
  num: 'SV-74220-1-000019', code: 'SV000019X', date: '30/06/2026', article: 'PANT F.RANDO STRETCH',
  size: 'T. 38', presta: 'Ourlet + reprise taille', priceCents: 1200, client: 'DUPONT Marie',
  pickup: '06/07/2026', obs: 'Marquer à la craie, -3 cm',
};
